import { z } from '@dcp/shared'

/**
 * Shared Zod schema for the Add and Edit Pack forms (DCP-171).
 *
 * Both dialogs bind the same two fields and need identical client-side rules:
 *
 *   - `name`         : required, whitespace-only rejected via `.trim().min(1)`.
 *                      Capped at 255 chars (see note) — AC #3.
 *   - `facility_ids` : at least one facility must be selected — AC #3. The
 *                      multi-select only offers facilities that are not already
 *                      in another pack (plus, when editing, the pack's own
 *                      members), so AC #4 ("no facility in two packs") is
 *                      enforced both at the UI and, ultimately, by the single
 *                      `facilities.pack_id` column.
 *
 * **Length cap.** `packs.name` is PostgreSQL `TEXT` (unbounded), but capping
 * at 255 mirrors the facility form and turns oversized paste input into a
 * precise field-level error instead of a silent DB bloat.
 *
 * Single source of truth so a future field addition lands in one place and
 * both dialogs pick it up via the shared `z.infer`.
 */
const MAX_PACK_NAME_LENGTH = 255

export const packFormSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, 'Pack name is required')
    .max(MAX_PACK_NAME_LENGTH, `Name must be ${MAX_PACK_NAME_LENGTH} characters or fewer`),
  facility_ids: z.array(z.string()).min(1, 'Select at least one facility'),
})

/**
 * Form-shape inferred from {@link packFormSchema}. Used as the generic
 * argument to `useForm<PackFormData>(…)` in both pack dialogs.
 */
export type PackFormData = z.infer<typeof packFormSchema>
